/**
 * Period presets shared by Dashboard and Payments (aligned with Analytics options,
 * using API-friendly values for presets and `Custom` for the date-range picker).
 */
export const DASHBOARD_PERIOD_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'By Week (Sun-Sat)' },
  { value: 'month', label: 'By Month' },
  { value: 'year', label: 'By Year' },
  { value: 'Custom', label: 'Custom' }
] as const;

export type DashboardPeriodFilter = (typeof DASHBOARD_PERIOD_OPTIONS)[number]['value'];

export interface DashboardPeriodSelection {
  period: DashboardPeriodFilter;
  startDate?: string;
  endDate?: string;
}

export function dashboardPeriodLabel(value: string | null | undefined): string {
  if (!value) return 'Today';
  if (value === 'Custom' || value.startsWith('Custom:')) return 'Custom';
  return DASHBOARD_PERIOD_OPTIONS.find(option => option.value === value)?.label ?? 'Today';
}

export function formatDateForPeriodApi(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function buildPeriodApiParam(
  period: string,
  customRange?: { startDate: Date | null; endDate: Date | null } | null
): string {
  if (period === 'Custom' && customRange?.startDate && customRange?.endDate) {
    return `Custom:${formatDateForPeriodApi(customRange.startDate)}:${formatDateForPeriodApi(customRange.endDate)}`;
  }
  return period;
}

export function parsePeriodApiParam(period: string): {
  filterBy: string;
  startDate?: string;
  endDate?: string;
  displayPeriod: string;
} {
  const sanitized = (period || 'today').trim();

  if (sanitized.startsWith('Custom')) {
    const [, startDate = '', endDate = ''] = sanitized.split(':');
    return {
      filterBy: 'custom',
      startDate: startDate || undefined,
      endDate: endDate || startDate || undefined,
      displayPeriod: 'Custom'
    };
  }

  const periodMap: Record<string, string> = {
    today: 'today',
    week: 'week',
    month: 'month',
    year: 'year',
    Custom: 'custom'
  };

  return {
    filterBy: periodMap[sanitized] ?? 'week',
    displayPeriod: sanitized
  };
}
